import { Component } from '@theme/component';
class DlBackground extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }
  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }
  initialize() {
    this.releaseResources();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.video = this.querySelector('video[data-background-video]');
    this.chart = this.querySelector('.dl-astrolabe__canvas');
    this.chartPointer = { x: 0, y: 0, targetX: 0, targetY: 0 };
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.userPaused = this.reduced.matches;
    this.pageLeaving = false;
    this.restored = false;
    this.waitingForSeek = false;
    this.playFailed = false;
    this.dataset.videoReady = 'false';
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && !this.pageLeaving) this.savePlayback(false);
      this.sync();
    }, { signal });
    window.addEventListener('pagehide', () => {
      this.savePlayback(!this.userPaused && !this.playFailed);
      this.pageLeaving = true;
      this.sync();
    }, { signal });
    window.addEventListener('pageshow', (event) => {
      this.pageLeaving = false;
      if (event.persisted && this.video?.readyState >= 1) {
        this.restored = false;
        this.restorePlayback();
      }
      this.sync();
    }, { signal });
    this.reduced.addEventListener('change', (event) => this.setPaused(event.matches), { signal });
    if (this.chart) {
      this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');
      document.addEventListener('dl:navigation', () => this.refreshChartRoute(), { signal });
      window.addEventListener('scroll', () => this.scheduleChartVisibility(), { passive: true, signal });
      window.addEventListener('resize', () => this.scheduleChartVisibility(), { passive: true, signal });
      window.addEventListener('pointermove', (event) => this.moveChartPointer(event), { passive: true, signal });
      window.addEventListener('pointerout', (event) => {
        if (event.relatedTarget === null) this.centerChartPointer();
      }, { signal });
      this.finePointer.addEventListener('change', () => this.centerChartPointer(), { signal });
      this.refreshChartRoute();
    }
    if (this.video) {
      this.video.muted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.video.addEventListener('loadedmetadata', () => this.restorePlayback(), { signal });
      this.video.addEventListener('loadeddata', () => this.playVideo(), { signal });
      this.video.addEventListener('seeked', () => {
        this.waitingForSeek = false;
        this.playVideo();
      }, { signal });
      this.video.addEventListener('playing', () => {
        if (!this.isPaused() && !this.waitingForSeek) this.dataset.videoReady = 'true';
      }, { signal });
      this.video.addEventListener('error', () => {
        this.dataset.videoReady = 'false';
        this.playFailed = true;
      }, { signal });
      if (this.video.readyState >= 1) this.restorePlayback();
    }
    this.sync();
  }
  isPaused() {
    return this.userPaused || document.hidden || this.pageLeaving;
  }
  setPaused(paused) {
    this.userPaused = paused;
    if (!paused) {
      this.playFailed = false;
      if (this.video?.error) {
        this.restored = false;
        this.video.load();
      }
    }
    this.savePlayback(!paused);
    this.sync();
  }
  sync() {
    const paused = this.isPaused();
    if (this.dataset.paused !== String(paused)) this.dataset.paused = String(paused);
    this.syncChartMotion();
    if (paused) {
      this.cancelStart();
      this.playRequest = (this.playRequest || 0) + 1;
      this.playPending = false;
      this.video?.pause();
      return;
    }
    if (!this.video || this.playFailed) return;
    if (!this.video.getAttribute('src')) this.scheduleStart();
    else if (this.video.readyState >= 1 && !this.restored) this.restorePlayback();
    else this.playVideo();
  }
  refreshChartRoute() {
    this.homeChart = document.body.classList.contains('dl-template-index');
    this.homeHero = this.homeChart ? document.querySelector('dl-bead-hero') : null;
    this.chartWidth = window.innerWidth;
    this.chartHeight = window.innerHeight;
    this.refreshChartVisibility();
    this.centerChartPointer();
  }
  scheduleChartVisibility() {
    if (!this.chart || this.chartVisibilityFrame) return;
    this.chartVisibilityFrame = requestAnimationFrame(() => {
      this.chartVisibilityFrame = 0;
      this.chartWidth = window.innerWidth;
      this.chartHeight = window.innerHeight;
      this.refreshChartVisibility();
    });
  }
  refreshChartVisibility() {
    // Do not run decorative layers while the opaque opening gallery covers them.
    this.chartCovered = Boolean(this.homeHero?.isConnected &&
      this.homeHero.getBoundingClientRect().bottom >= this.chartHeight - 4);
    this.syncChartMotion();
  }
  syncChartMotion() {
    if (!this.chart) return;
    const paused = this.isPaused() || this.reduced.matches || this.chartCovered;
    if (this.dataset.chartPaused !== String(paused)) this.dataset.chartPaused = String(paused);
    if (paused || !this.homeChart) this.centerChartPointer(true);
  }
  moveChartPointer(event) {
    if (!this.homeChart || !this.finePointer.matches || this.reduced.matches ||
        this.isPaused() || this.chartCovered || event.pointerType === 'touch') return;
    const clamp = (value) => Math.max(-1, Math.min(1, value));
    this.chartPointer.targetX = clamp(event.clientX / Math.max(1, this.chartWidth) * 2 - 1);
    this.chartPointer.targetY = clamp(event.clientY / Math.max(1, this.chartHeight) * 2 - 1);
    this.animateChartPointer();
  }
  centerChartPointer(immediate = false) {
    if (!this.chartPointer) return;
    this.chartPointer.targetX = this.chartPointer.targetY = 0;
    if (immediate || this.reduced.matches || !this.homeChart || this.isPaused()) {
      if (this.chartPointerFrame) cancelAnimationFrame(this.chartPointerFrame);
      this.chartPointerFrame = 0;
      this.chartPointer.x = this.chartPointer.y = 0;
      this.paintChartPointer();
    } else this.animateChartPointer();
  }
  animateChartPointer() {
    if (this.chartPointerFrame) return;
    this.chartPointerFrame = requestAnimationFrame(() => {
      this.chartPointerFrame = 0;
      const pointer = this.chartPointer;
      pointer.x += (pointer.targetX - pointer.x) * .12;
      pointer.y += (pointer.targetY - pointer.y) * .12;
      const settled = Math.abs(pointer.targetX - pointer.x) + Math.abs(pointer.targetY - pointer.y) < .002;
      if (settled) {
        pointer.x = pointer.targetX;
        pointer.y = pointer.targetY;
      }
      this.paintChartPointer();
      // Rest completely between input events; the slow orbits stay on compositor layers.
      if (!settled) this.animateChartPointer();
    });
  }
  paintChartPointer() {
    const { x, y } = this.chartPointer;
    const next = `${x.toFixed(3)},${y.toFixed(3)}`;
    if (!this.chart || this.chartPaint === next) return;
    this.chartPaint = next;
    this.chart.style.setProperty('--dl-chart-x', `${(x * 7).toFixed(2)}px`);
    this.chart.style.setProperty('--dl-chart-y', `${(y * 5).toFixed(2)}px`);
    this.chart.style.setProperty('--dl-chart-tilt-x', `${(-y * 2.2).toFixed(2)}deg`);
    this.chart.style.setProperty('--dl-chart-tilt-y', `${(x * 2.8).toFixed(2)}deg`);
  }
  scheduleStart() {
    if (this.startFrame || this.startIdle || this.startTimer) return;
    // Let the poster and page content paint before starting the decorative decode.
    this.startFrame = requestAnimationFrame(() => {
      this.startFrame = 0;
      if (this.isPaused() || this.controller.signal.aborted) return;
      if (typeof window.requestIdleCallback === 'function') {
        this.startIdle = window.requestIdleCallback(() => this.startVideo(), { timeout: 500 });
      } else {
        this.startTimer = setTimeout(() => this.startVideo(), 250);
      }
    });
  }
  startVideo() {
    this.startIdle = 0;
    this.startTimer = 0;
    if (this.isPaused() || this.controller.signal.aborted || !this.video?.dataset.src) return;
    this.video.preload = 'auto';
    this.video.src = this.video.dataset.src;
    this.video.load();
  }
  sourceKey() {
    if (!this.video?.dataset.src) return '';
    try {
      const url = new URL(this.video.dataset.src, window.location.href);
      return `${url.origin}${url.pathname}`;
    } catch { return this.video.dataset.src; }
  }
  restorePlayback() {
    const video = this.video;
    if (!video || this.restored || video.readyState < 1) return;
    this.restored = true;
    try {
      const saved = JSON.parse(sessionStorage.getItem('dl-background-playback') || 'null');
      if (saved?.source === this.sourceKey() && Number.isFinite(saved.time) &&
          Number.isFinite(saved.savedAt) && Number.isFinite(video.duration) && video.duration > 0) {
        const elapsed = saved.running ? Math.max(0, (Date.now() - saved.savedAt) / 1000) : 0;
        const target = (Math.max(0, saved.time) + elapsed) % video.duration;
        if (Math.abs(video.currentTime - target) > 0.05) {
          this.waitingForSeek = true;
          video.currentTime = target;
          this.waitingForSeek = video.seeking;
        }
      }
    } catch { this.waitingForSeek = false; }
    this.playVideo();
  }
  playVideo() {
    const video = this.video;
    if (!video || this.isPaused() || this.playFailed || this.playPending ||
        !this.restored || this.waitingForSeek || video.seeking || video.readyState < 2 || !video.paused) return;
    const request = this.playRequest = (this.playRequest || 0) + 1;
    const { signal } = this.controller;
    this.playPending = true;
    try {
      Promise.resolve(video.play()).catch(() => {
        if (!signal.aborted && request === this.playRequest && !this.isPaused()) {
          this.dataset.videoReady = 'false';
          this.playFailed = true;
        }
      }).finally(() => {
        if (!signal.aborted && request === this.playRequest) this.playPending = false;
      });
    } catch {
      this.playPending = false;
      this.playFailed = true;
      this.dataset.videoReady = 'false';
    }
  }
  savePlayback(running) {
    if (!this.video || !this.restored || this.video.readyState < 1 ||
        this.waitingForSeek || !Number.isFinite(this.video.currentTime)) return;
    try {
      sessionStorage.setItem('dl-background-playback', JSON.stringify({
        source: this.sourceKey(),
        time: this.video.currentTime,
        savedAt: Date.now(),
        running: Boolean(running),
      }));
    } catch {}
  }
  cancelStart() {
    if (this.startFrame) cancelAnimationFrame(this.startFrame);
    if (this.startIdle && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(this.startIdle);
    if (this.startTimer) clearTimeout(this.startTimer);
    this.startFrame = this.startIdle = this.startTimer = 0;
  }
  releaseResources() {
    if (!this.pageLeaving) this.savePlayback(!this.userPaused && !this.playFailed);
    this.cancelStart();
    if (this.chartPointerFrame) cancelAnimationFrame(this.chartPointerFrame);
    if (this.chartVisibilityFrame) cancelAnimationFrame(this.chartVisibilityFrame);
    this.chartPointerFrame = this.chartVisibilityFrame = 0;
    this.chartPaint = null;
    this.controller?.abort();
    this.playRequest = (this.playRequest || 0) + 1;
    this.playPending = false;
    this.video?.pause();
  }
  disconnectedCallback() {
    this.releaseResources();
    this.dataset.paused = 'true';
    super.disconnectedCallback();
  }
}
class DlLatticeTest extends Component {
  connectedCallback(){
    super.connectedCallback();this.controller?.abort();this.controller=new AbortController();
    const form=this.querySelector('[data-test-form]'),loading=this.querySelector('.dl-test-loading'),pending=this.querySelector('.dl-test-pending');
    const date=form?.querySelector('[name=birth_date]');if(date)date.max=new Date().toLocaleDateString('en-CA');
    form?.addEventListener('submit',event=>{event.preventDefault();if(!form.reportValidity())return;form.hidden=true;loading.hidden=false;clearTimeout(this.timer);this.timer=setTimeout(()=>{loading.hidden=true;pending.hidden=false;pending.focus({preventScroll:true});},Number(this.dataset.duration)||2500);},{signal:this.controller.signal});
    const submit=form?.querySelector('[data-test-submit]');if(submit)submit.disabled=false;
    this.querySelector('[data-test-reset]')?.addEventListener('click',()=>{pending.hidden=true;form.hidden=false;form.querySelector('input')?.focus({preventScroll:true});},{signal:this.controller.signal});
  }
  disconnectedCallback(){super.disconnectedCallback();this.controller?.abort();clearTimeout(this.timer);}
}
if(!customElements.get('dl-background'))customElements.define('dl-background',DlBackground);
if(!customElements.get('dl-lattice-test'))customElements.define('dl-lattice-test',DlLatticeTest);
document.addEventListener('keydown',event=>{if(event.key==='Escape')document.querySelectorAll('.dl-mobile-menu[open]').forEach(el=>el.removeAttribute('open'));});
document.addEventListener('click',event=>{document.querySelectorAll('.dl-mobile-menu[open]').forEach(el=>{if(!el.contains(event.target))el.removeAttribute('open');});});
